"use client";

import type { PropsWithChildren } from "react";
import { Suspense, useMemo } from "react";

import Image from "next/image";

import { WithdrawalFormModal } from "@jti/ui/components/WithdrawalFormModal";
import { Footer as UIFooter, WITHDRAWAL_MODAL_TRIGGER_URL } from "@jti/ui/eva-components/Footer";
import type { FooterNavLink } from "@jti/ui/eva-components/Footer/types/FooterNavLink.type";
import type { RenderLink } from "@jti/ui/eva-components/types/RenderLink.type";

import { FooterClientEditingZone } from "@/components/Footer/components/FooterClientEditingZone/FooterClientEditingZone";
import { usePageConfig } from "@/context/PageConfigContext";
import { useIdleCallback } from "@/hooks/useIdleCallback";
import { Link } from "@/libs/i18n";

import { useFooterWithdrawal } from "./hooks/useFooterWithdrawal";
import type { FooterProps } from "./inputs/footerInputs";

import styles from "./Footer.module.scss";

type EditingZoneKey = "firstEditingZone" | "secondEditingZone" | "topEditingZone" | "topBannerEditingZone";
export type EditingZone = FooterProps[EditingZoneKey];

const filterWithdrawalTriggerLinks = (links: FooterNavLink[], shouldShowWithdrawalTrigger: boolean) =>
  links.filter((link) => shouldShowWithdrawalTrigger || link.url !== WITHDRAWAL_MODAL_TRIGGER_URL);

export const Footer = (props: PropsWithChildren<FooterProps>) => {
  const {
    children,
    builderBlock,
    topEditingZone,
    firstEditingZone,
    secondEditingZone,
    socialIcons,
    paymentIcons,
    enableTopBanner,
    topBannerEditingZone,
    withListComponent,
    theme,
    ...restProps
  } = props;

  useIdleCallback(() => window?.OneTrust?.initializeCookiePolicyHtml?.(), 0);

  const {
    isWithdrawalEnabledForCurrentUser,
    isLoggedIn,
    withdrawalStep,
    withdrawalError,
    withdrawalSubmittedOn,
    isWithdrawalFormLoading,
    openWithdrawalModal,
    closeWithdrawalModal,
    handleWithdrawalConfirmUnauthenticated,
    handleWithdrawalConfirmAuthenticated,
  } = useFooterWithdrawal();

  const { breadcrumbsList = [], hideBreadcrumbsOnFooter } = usePageConfig();
  const parentId = builderBlock?.id;

  const renderSocialIcon = ({ src, alt }: { src: string; alt: string }) => (
    <Image src={src} alt={alt} width={32} height={32} className={styles.iconImage} />
  );

  const breadcrumbs = hideBreadcrumbsOnFooter ? [] : breadcrumbsList;
  const isLogoDarkMode = theme === "dark" || !theme;
  const shouldShowWithdrawalTriggerLink = Boolean(isWithdrawalEnabledForCurrentUser);

  const filteredFooterNavProps = useMemo(
    () => ({
      ...restProps.footerNavProps,
      subNavs:
        restProps.footerNavProps.subNavs?.map((subNav) => ({
          ...subNav,
          links: filterWithdrawalTriggerLinks(subNav.links, shouldShowWithdrawalTriggerLink),
        })) ?? [],
      subNavsInlineEndSection: restProps.footerNavProps.subNavsInlineEndSection
        ? {
            ...restProps.footerNavProps.subNavsInlineEndSection,
            links: filterWithdrawalTriggerLinks(
              restProps.footerNavProps.subNavsInlineEndSection.links,
              shouldShowWithdrawalTriggerLink
            ),
          }
        : undefined,
    }),
    [restProps.footerNavProps, shouldShowWithdrawalTriggerLink]
  );

  const filteredCopyrightProps = useMemo(
    () => ({
      ...restProps.copyrightProps,
      links: filterWithdrawalTriggerLinks(restProps.copyrightProps.links ?? [], shouldShowWithdrawalTriggerLink),
    }),
    [restProps.copyrightProps, shouldShowWithdrawalTriggerLink]
  );

  return (
    <>
      <UIFooter
        {...restProps}
        footerNavProps={filteredFooterNavProps}
        copyrightProps={filteredCopyrightProps}
        theme={theme}
        renderLink={renderLink}
        breadcrumbsList={breadcrumbs}
        logoProps={{ ...props.logoProps, isDarkMode: isLogoDarkMode }}
        renderFooterTopZone={() => (
          <Suspense>
            <FooterClientEditingZone editingZone={topEditingZone} propPath="topEditingZone" parentId={parentId} />
          </Suspense>
        )}
        renderFirstBottomZone={() => (
          <Suspense>
            <FooterClientEditingZone editingZone={firstEditingZone} propPath="firstEditingZone" parentId={parentId} />
          </Suspense>
        )}
        renderSecondBottomZone={() => (
          <Suspense>
            <FooterClientEditingZone editingZone={secondEditingZone} propPath="secondEditingZone" parentId={parentId} />
          </Suspense>
        )}
        socialIcons={socialIcons}
        paymentIcons={paymentIcons}
        renderSocialIcon={renderSocialIcon}
        enableTopBanner={enableTopBanner}
        renderTopBanner={() => (
          <Suspense>
            <FooterClientEditingZone
              editingZone={topBannerEditingZone}
              propPath="topBannerEditingZone"
              parentId={parentId}
            />
          </Suspense>
        )}
        withListComponent={withListComponent}
        withdrawalModalTriggerUrl={WITHDRAWAL_MODAL_TRIGGER_URL}
        onOpenWithdrawalModal={isWithdrawalEnabledForCurrentUser ? openWithdrawalModal : undefined}
      >
        {children}
      </UIFooter>

      {isWithdrawalEnabledForCurrentUser && withdrawalStep && (
        <WithdrawalFormModal
          onClose={closeWithdrawalModal}
          step={withdrawalStep}
          onConfirm={isLoggedIn ? handleWithdrawalConfirmAuthenticated : handleWithdrawalConfirmUnauthenticated}
          submittedOn={withdrawalSubmittedOn}
          isLoading={isWithdrawalFormLoading}
          error={withdrawalError}
        />
      )}
    </>
  );
};

// TODO: should be replaced with LinkBehaviour context
export const renderLink = (props: RenderLink): JSX.Element | null => {
  const { href, ...rest } = props;
  if (!href) return null;
  return (
    <Link href={href} {...rest}>
      {props.children}
    </Link>
  );
};

export default Footer;
